import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowSquareOutIcon, PlusIcon, PackageIcon } from "@phosphor-icons/react";

import Layout from "../../components/Layout";
import EntityHeader from "../../components/crm/EntityHeader";
import EmptyState from "../../components/EmptyState";
import SharedSkeletonRows from "../../components/SkeletonRows";
import { ErrorBanner, INPUT_CLS, PrimaryButton } from "../../components/crm/Form";
import RMAStatusBadge from "../../components/rma/RMAStatusBadge";
import CreateRMAModal from "../../components/rma/CreateRMAModal";
import { formatApiError } from "../../lib/api";
import {
  getRMARecords,
  RMA_COMPONENT_LABEL,
  RMA_STATUSES,
  RMA_STATUS_LABEL,
  RMA_VENDORS,
  RMA_VENDOR_LABEL,
  relativeTime,
  shortId,
} from "../../lib/rma";

const PAGE_SIZE = 25;

const STATUS_FILTERS = [
  { value: "", label: "All" },
  ...RMA_STATUSES.map((s) => ({ value: s, label: RMA_STATUS_LABEL[s] })),
];

const VENDOR_FILTERS = [
  { value: "", label: "All" },
  ...RMA_VENDORS.map((v) => ({ value: v, label: RMA_VENDOR_LABEL[v] })),
];

export default function RMARecordsList() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const status = params.get("status") || "";
  const vendor = params.get("vendor") || "";
  const search = params.get("search") || "";
  const page = Number(params.get("page") || "1") || 1;

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [searchInput, setSearchInput] = useState(search);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const data = await getRMARecords({
        ...(status ? { status } : {}),
        ...(vendor ? { vendor } : {}),
        ...(search ? { search } : {}),
        page,
        page_size: PAGE_SIZE,
      });
      setItems(data?.items || []);
      setTotal(data?.total || 0);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [status, vendor, search, page]);

  useEffect(() => {
    load();
  }, [load]);

  // Phase 15 — auto-open create modal when navigated with ?new=1 (or ?new=rma).
  useEffect(() => {
    const newVal = params.get("new");
    if (newVal === "1" || newVal === "rma") {
      setCreateOpen(true);
      const next = new URLSearchParams(params);
      next.delete("new");
      setParams(next, { replace: true });
    }
  }, [params, setParams]);

  // Sync local search input with URL when other filters change externally.
  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  // Debounce: push searchInput → URL after 300ms of no typing.
  useEffect(() => {
    const handle = setTimeout(() => {
      if (searchInput === search) return;
      const next = new URLSearchParams(params);
      if (searchInput) next.set("search", searchInput);
      else next.delete("search");
      next.delete("page");
      setParams(next);
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    next.delete("page");
    setParams(next);
  };

  const setPage = (p) => {
    const next = new URLSearchParams(params);
    if (p && p !== 1) next.set("page", String(p));
    else next.delete("page");
    setParams(next);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Layout>
      <div className="space-y-6">
        <EntityHeader
          title="RMA & Warranty Claims"
          subtitle="Track vendor warranty claims and replacement workflows"
          actions={
            <PrimaryButton
              onClick={() => setCreateOpen(true)}
              data-testid="rma-list-new-btn"
            >
              <PlusIcon size={16} className="mr-1.5" /> New RMA
            </PrimaryButton>
          }
        />
        <ErrorBanner message={err} />

        <div className="bg-white border border-slate-200 rounded-md p-4 space-y-3">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by RMA #, vendor case #, or component serial…"
            data-testid="rma-list-search"
            className={INPUT_CLS}
          />
          <FilterChipRow
            label="Status"
            options={STATUS_FILTERS}
            value={status}
            onChange={(v) => setFilter("status", v)}
            testidPrefix="rma-status"
          />
          <FilterChipRow
            label="Vendor"
            options={VENDOR_FILTERS}
            value={vendor}
            onChange={(v) => setFilter("vendor", v)}
            testidPrefix="rma-vendor"
          />
          <div className="text-[11px] text-slate-500 pt-1">
            Showing {items.length} of {total}
          </div>
        </div>

        <div
          className="bg-white border border-slate-200 rounded-md overflow-hidden"
          data-testid="rma-list"
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-left text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                <th className="px-4 py-2.5">RMA #</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5">Vendor</th>
                <th className="px-4 py-2.5">Component</th>
                <th className="px-4 py-2.5">Customer</th>
                <th className="px-4 py-2.5">Property</th>
                <th className="px-4 py-2.5">Vendor Case #</th>
                <th className="px-4 py-2.5">Created</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 && (
                <SharedSkeletonRows count={5} cols={9} />
              )}
              {!loading && items.length === 0 && (
                <EmptyState
                  inTable
                  colSpan={9}
                  icon={PackageIcon}
                  title={
                    status || vendor || search
                      ? "No matching RMA claims"
                      : "No RMA claims yet"
                  }
                  description={
                    status || vendor || search
                      ? "Try clearing the filters above to see all RMA claims."
                      : "Open a piece of equipment with a manufacturer warranty and click 'Start RMA' to begin a return claim."
                  }
                  cta={
                    !(status || vendor || search)
                      ? {
                          label: "New RMA",
                          onClick: () => setCreateOpen(true),
                          testId: "rma-empty-cta",
                        }
                      : null
                  }
                  testId="rma-empty"
                />
              )}
              {items.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`rma-row-${r.id}`}
                  onClick={() => navigate(`/rma-records/${r.id}`)}
                  className="border-t border-slate-200 hover:bg-slate-50/70 cursor-pointer"
                >
                  <td className="px-4 py-2.5 align-top font-data text-xs text-slate-900">
                    {r.rma_number}
                  </td>
                  <td className="px-4 py-2.5 align-top">
                    <RMAStatusBadge status={r.status} />
                  </td>
                  <td className="px-4 py-2.5 align-top text-xs text-slate-700">
                    {RMA_VENDOR_LABEL[r.vendor] || r.vendor}
                  </td>
                  <td className="px-4 py-2.5 align-top text-xs text-slate-700">
                    {RMA_COMPONENT_LABEL[r.component_type] || r.component_type}
                  </td>
                  <td className="px-4 py-2.5 align-top text-sm text-slate-900">
                    {r.customer_id ? (
                      <Link
                        to={`/customers/${r.customer_id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="hover:underline"
                      >
                        {r.customer_name || "—"}
                      </Link>
                    ) : (
                      r.customer_name || "—"
                    )}
                  </td>
                  <td className="px-4 py-2.5 align-top text-xs text-slate-700">
                    {r.property_address || "—"}
                  </td>
                  <td className="px-4 py-2.5 align-top text-xs text-slate-700 font-data">
                    {/* vendor_case_number isn't on the slim list response;
                        omit until detail. Shown only if API ever surfaces it. */}
                    {r.vendor_case_number || "—"}
                  </td>
                  <td className="px-4 py-2.5 align-top text-xs text-slate-700">
                    <span title={r.created_at}>
                      {relativeTime(r.created_at)}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 align-top text-right">
                    <Link
                      to={`/rma-records/${r.id}`}
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`rma-row-open-${r.id}`}
                      title={`Open RMA ${shortId(r.id)}`}
                      className="rounded-md border border-slate-200 hover:border-slate-400 p-1.5 transition inline-flex"
                    >
                      <ArrowSquareOutIcon size={14} className="text-slate-700" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-slate-200 px-4 py-2.5 text-xs">
              <span className="text-slate-500">
                Page {page} of {totalPages}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page <= 1}
                  className="rounded-md border border-slate-300 px-3 py-1 hover:border-slate-500 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="rma-list-prev"
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page >= totalPages}
                  className="rounded-md border border-slate-300 px-3 py-1 hover:border-slate-500 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="rma-list-next"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <CreateRMAModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </Layout>
  );
}

function FilterChipRow({ label, options, value, onChange, testidPrefix }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mr-1 w-16">
        {label}
      </span>
      {options.map((o) => {
        const isActive = value === o.value || (!value && o.value === "");
        return (
          <button
            key={o.value || "_all"}
            type="button"
            data-testid={`${testidPrefix}-${o.value || "all"}`}
            onClick={() => onChange(o.value)}
            className={`text-xs px-2.5 py-1 rounded-md border transition ${
              isActive
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function SkeletonRowsLegacy() {
  // Phase 15 — superseded by shared SkeletonRows. Kept as no-op for diff
  // hygiene; safe to delete in a follow-up cleanup pass.
  return null;
}
void SkeletonRowsLegacy;
